import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import '../styles/formularios.css';
import '../styles/styles.css';
import Swal from 'sweetalert2';
import { useLocation, useNavigate } from 'react-router-dom';

import logoInstituto from '../components/images/logo.png';
import logoSEP from '../components/images/SEP_Logo_2019.svg.png.jpg';
import { resolveApiBaseList } from '../utils/apiBase';

const ID_COORDINADORA_FALLBACK = 14;

const camposIniciales = {
  idUsuario: '',
  tipoUsuario: 'Coordinadora',
  nombre: '',
  apellidoPaterno: '',
  apellidoMaterno: '',
  sexo: 'F',
  telefono: '',
  correo: '',
  fechaNacimiento: '',
  puesto: 'Coordinadora'
};

const parsePositiveNumber = (valor) => {
  const numero = Number(valor);
  return Number.isFinite(numero) && numero > 0 ? numero : null;
};

const obtenerIdInicialCoordinadora = () => {
  if (typeof window !== 'undefined') {
    const almacenado = parsePositiveNumber(window.localStorage.getItem('coordinadoraId'));
    if (almacenado) {
      return almacenado;
    }
  }

  const desdeEntorno = parsePositiveNumber(process.env.REACT_APP_COORDINADORA_ID);
  if (desdeEntorno) {
    return desdeEntorno;
  }

  return ID_COORDINADORA_FALLBACK;
};

const construirNombreCompleto = (registro = {}) => {
  return [registro.nombre, registro.apellidoPaterno, registro.apellidoMaterno]
    .map((parte) => (parte || '').trim())
    .filter(Boolean)
    .join(' ');
};

const ConsultarCoordinador = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [datos, setDatos] = useState(camposIniciales);
  const [loading, setLoading] = useState(true);
  const [isModified, setIsModified] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    contrasenaActual: '',
    nuevaContrasena: '',
    confirmarContrasena: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    actual: false,
    nueva: false,
    confirmar: false
  });
  const [selectedId, setSelectedId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searchingSuggestions, setSearchingSuggestions] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const blurTimeoutRef = useRef(null);
  const apiBases = useMemo(() => resolveApiBaseList(), []);

  const contrasenasCoinciden = useMemo(
    () =>
      passwordData.nuevaContrasena.trim() === passwordData.confirmarContrasena.trim(),
    [passwordData.nuevaContrasena, passwordData.confirmarContrasena]
  );

  const fetchJsonWithFallback = useCallback(async (path, options = {}) => {
    let lastError = null;
    for (const base of apiBases) {
      if (options?.signal?.aborted) {
        const abortError = new DOMException('The operation was aborted.', 'AbortError');
        throw abortError;
      }

      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;
      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch (_parseErr) {
            // mantener mensaje original
          }
          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`⚠️ Falló la petición a ${url}:`, error);
          continue;
        }

        const contentType = response.headers.get('content-type') || '';
        const trimmed = text.trim();
        if (!trimmed) {
          return {};
        }

        if (contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
          return JSON.parse(trimmed);
        }

        try {
          return JSON.parse(trimmed);
        } catch (_parseErr) {
          lastError = new Error(`Respuesta inválida desde ${url}`);
          console.warn(`⚠️ Contenido no JSON desde ${url} (content-type: ${contentType || 'desconocido'})`);
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          throw error;
        }
        lastError = error;
        console.warn(`⚠️ Error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  const sendJsonWithFallback = useCallback(
    async (path, body = {}, { method = 'POST', headers = {}, ...rest } = {}) => {
      const payload = JSON.stringify(body);
      return fetchJsonWithFallback(path, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: payload,
        ...rest,
      });
    },
    [fetchJsonWithFallback]
  );

  const cargarDatos = useCallback(async (idObjetivo) => {
    const id = parsePositiveNumber(idObjetivo);

    if (!id) {
      Swal.fire({
        title: 'ID requerido',
        text: 'Proporciona un identificador válido para la coordinadora.',
        icon: 'warning'
      });
      return;
    }

    try {
      setLoading(true);

      const cuerpo = await fetchJsonWithFallback(`/coordinadora?idUsuario=${id}`);

      if (!cuerpo?.success || !cuerpo.coordinadora) {
        throw new Error(cuerpo?.error || 'No fue posible obtener la información de la coordinadora.');
      }

      const datosCoordinadora = cuerpo.coordinadora;

      setDatos({
        idUsuario: datosCoordinadora.idUsuario ?? id,
        tipoUsuario: datosCoordinadora.tipoUsuario || 'Coordinadora',
        nombre: datosCoordinadora.nombre ?? '',
        apellidoPaterno:
          datosCoordinadora.apellidoPaterno?.length
            ? datosCoordinadora.apellidoPaterno
            : datosCoordinadora.usuario ?? '',
        apellidoMaterno: datosCoordinadora.apellidoMaterno ?? '',
        sexo: datosCoordinadora.sexo || '',
        telefono: datosCoordinadora.telefono ?? '',
        correo: datosCoordinadora.correo ?? '',
        fechaNacimiento: datosCoordinadora.fechaNacimiento
          ? datosCoordinadora.fechaNacimiento.substring(0, 10)
          : '',
        puesto: datosCoordinadora.puesto ?? 'Coordinadora'
      });

      setIsModified(false);

      const nombreVisible = construirNombreCompleto(datosCoordinadora);
      const etiquetaBusqueda = nombreVisible || datosCoordinadora.usuario || `Coordinadora ${id}`;

      setSelectedId(String(id));
      setSearchTerm(etiquetaBusqueda);
      setSuggestions([]);
      setSearchError('');
      setShowSuggestions(false);
      setSearchingSuggestions(false);

      if (typeof window !== 'undefined') {
        window.localStorage.setItem('coordinadoraId', String(id));
      }
    } catch (error) {
      console.error('❌ Error al cargar la información de la coordinadora desde la interfaz de directora:', error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Ocurrió un error al cargar la información.',
        icon: 'error'
      });
      setDatos(camposIniciales);
    } finally {
      setLoading(false);
    }
  }, [fetchJsonWithFallback]);

  useEffect(() => {
    const idInicial = obtenerIdInicialCoordinadora();
    if (idInicial) {
      cargarDatos(idInicial);
    } else {
      setLoading(false);
    }
  }, [cargarDatos]);

  useEffect(() => {
    if (!showSuggestions) {
      return;
    }

    const termino = searchTerm.trim();

    if (termino.length < 2) {
      setSuggestions([]);
      setSearchError('');
      setSearchingSuggestions(false);
      return;
    }

    setSearchingSuggestions(true);
    setSearchError('');

    const controlador = new AbortController();
    const timeoutId = setTimeout(async () => {
      try {
        const cuerpo = await fetchJsonWithFallback(
          `/coordinadora/buscar?q=${encodeURIComponent(termino)}&limit=20`,
          { signal: controlador.signal }
        );

        if (!cuerpo?.success || !Array.isArray(cuerpo.resultados)) {
          throw new Error(cuerpo?.error || 'No fue posible obtener sugerencias.');
        }

        setSuggestions(cuerpo.resultados);
        setSearchError('');
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('❌ Error al buscar coordinadoras:', error);
          setSearchError(error.message || 'Ocurrió un error al obtener sugerencias.');
          setSuggestions([]);
        }
      } finally {
        setSearchingSuggestions(false);
      }
    }, 250);

    return () => {
      controlador.abort();
      clearTimeout(timeoutId);
    };
  }, [fetchJsonWithFallback, searchTerm, showSuggestions]);

  useEffect(
    () => () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    },
    []
  );

  const handleSalir = () => {
    const rol = resolveRolBase(location.pathname);
    if (rol) {
      navigate(`/${rol}/inicio`, { replace: true });
    } else {
      navigate('/inicio', { replace: true });
    }
  };

  const handleSearchFocus = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }

    if (searchTerm.trim().length >= 2) {
      setShowSuggestions(true);
    }
  };

  const handleSearchBlur = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }

    blurTimeoutRef.current = setTimeout(() => {
      setShowSuggestions(false);
    }, 150);
  };

  const handleSearchTermChange = (evento) => {
    const valor = evento.target.value;

    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }

    setSearchTerm(valor);
    setSearchError('');
    setSelectedId('');

    if (valor.trim().length >= 2) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
      setSuggestions([]);
      setSearchingSuggestions(false);
    }
  };

  const manejarSeleccionSugerencia = async (sugerencia) => {
    if (!sugerencia || !sugerencia.idUsuario) {
      return;
    }

    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }

    setShowSuggestions(false);
    setSuggestions([]);
    setSelectedId(String(sugerencia.idUsuario));
    setSearchTerm(sugerencia.displayName || construirNombreCompleto(sugerencia) || sugerencia.usuario || String(sugerencia.idUsuario));

    await cargarDatos(sugerencia.idUsuario);
  };

  const handleSearchSubmit = async (evento) => {
    evento.preventDefault();

    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }

    const idSeleccionado = parsePositiveNumber(selectedId);
    const idEscrito = parsePositiveNumber(searchTerm);

    let idObjetivo = idSeleccionado || idEscrito || null;

    if (!idObjetivo) {
      const terminoNormalizado = searchTerm.trim().toLowerCase();
      const coincidenciaExacta = suggestions.find((sugerencia) =>
        (sugerencia.displayName || '').toLowerCase() === terminoNormalizado
      );

      if (coincidenciaExacta) {
        idObjetivo = Number(coincidenciaExacta.idUsuario);
      } else if (suggestions.length === 1) {
        idObjetivo = Number(suggestions[0].idUsuario);
      }
    }

    if (idObjetivo) {
      setShowSuggestions(false);
      setSuggestions([]);
      setSearchError('');
      await cargarDatos(idObjetivo);
      return;
    }

    if (!searchTerm.trim()) {
      Swal.fire({
        title: 'Dato requerido',
        text: 'Escribe el nombre o selecciona una coordinadora.',
        icon: 'warning'
      });
      return;
    }

    Swal.fire({
      title: 'Selecciona una opción',
      text: 'Elige una coordinadora de la lista desplegable.',
      icon: 'info'
    });
  };

  const handleInputChange = (evento) => {
    const { name, value } = evento.target;
    setDatos((prev) => ({ ...prev, [name]: value }));
    setIsModified(true);
  };

  const handleTelefonoChange = (evento) => {
    const soloNumeros = evento.target.value.replace(/\D/g, '').slice(0, 10);
    setDatos((prev) => ({ ...prev, telefono: soloNumeros }));
    setIsModified(true);
  };

  const handleSubmit = async (evento) => {
    evento.preventDefault();

    try {
      if (!datos.idUsuario) {
        Swal.fire({
          title: 'Sin coordinadora seleccionada',
          text: 'Debes cargar primero la información de la coordinadora.',
          icon: 'warning'
        });
        return;
      }

      if (!isModified) {
        Swal.fire({
          title: 'Sin cambios',
          text: 'No se han realizado modificaciones.',
          icon: 'info'
        });
        return;
      }

      const confirmar = await Swal.fire({
        title: '¿Guardar cambios?',
        text: 'Se actualizará la información de la coordinadora.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, guardar',
        cancelButtonText: 'Cancelar'
      });

      if (!confirmar.isConfirmed) return;

      const cuerpo = await sendJsonWithFallback('/coordinadora', {
        idUsuario: Number(datos.idUsuario),
        nombre: datos.nombre,
        apellidoPaterno: datos.apellidoPaterno,
        apellidoMaterno: datos.apellidoMaterno,
        sexo: datos.sexo,
        telefono: datos.telefono,
        correo: datos.correo,
        fechaNacimiento: datos.fechaNacimiento,
        puesto: datos.puesto
      }, { method: 'PUT' });

      if (!cuerpo.success) {
        throw new Error(cuerpo.error || 'No fue posible guardar los cambios.');
      }

      const infoActualizada = cuerpo.coordinadora;

      setDatos({
        idUsuario: infoActualizada.idUsuario ?? datos.idUsuario,
        tipoUsuario: infoActualizada.tipoUsuario || 'Coordinadora',
        nombre: infoActualizada.nombre ?? '',
        apellidoPaterno: infoActualizada.apellidoPaterno ?? '',
        apellidoMaterno: infoActualizada.apellidoMaterno ?? '',
        sexo: infoActualizada.sexo || '',
        telefono: infoActualizada.telefono ?? '',
        correo: infoActualizada.correo ?? '',
        fechaNacimiento: infoActualizada.fechaNacimiento
          ? infoActualizada.fechaNacimiento.substring(0, 10)
          : '',
        puesto: infoActualizada.puesto ?? 'Coordinadora'
      });
      setIsModified(false);

      Swal.fire({
        title: '¡Éxito!',
        text: 'Información actualizada correctamente.',
        icon: 'success'
      });
    } catch (error) {
      console.error('❌ Error al actualizar información de la coordinadora desde la vista de directora:', error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Ocurrió un error al guardar los cambios.',
        icon: 'error'
      });
    }
  };

  const handlePasswordInputChange = (campo, valor) => {
    setPasswordData((prev) => ({ ...prev, [campo]: valor }));
  };

  const togglePasswordVisibility = (campo) => {
    setShowPasswords((prev) => ({ ...prev, [campo]: !prev[campo] }));
  };

  const abrirModalContrasena = () => {
    if (!datos.idUsuario) {
      Swal.fire({
        title: 'Sin coordinadora seleccionada',
        text: 'Debes cargar primero la información de la coordinadora.',
        icon: 'warning'
      });
      return;
    }

    setPasswordData({
      contrasenaActual: '',
      nuevaContrasena: '',
      confirmarContrasena: ''
    });
    setShowPasswordModal(true);
  };

  const cambiarContrasena = async () => {
    try {
      const contrasenaActual = passwordData.contrasenaActual.trim();
      const nuevaContrasena = passwordData.nuevaContrasena.trim();
      const confirmarContrasena = passwordData.confirmarContrasena.trim();

      if (!datos.idUsuario) {
        Swal.fire({
          title: 'Sin coordinadora seleccionada',
          text: 'Debes cargar primero la información de la coordinadora.',
          icon: 'warning'
        });
        return;
      }

      if (!contrasenaActual || !nuevaContrasena || !confirmarContrasena) {
        Swal.fire({
          title: 'Campos incompletos',
          text: 'Todos los campos son obligatorios.',
          icon: 'error'
        });
        return;
      }

      if (nuevaContrasena !== confirmarContrasena) {
        Swal.fire({
          title: 'Contraseñas diferentes',
          text: 'Las contraseñas nuevas no coinciden.',
          icon: 'error'
        });
        return;
      }

      const cuerpo = await sendJsonWithFallback('/coordinadora/cambiar-contrasena', {
        idUsuario: Number(datos.idUsuario),
        contrasenaActual,
        nuevaContrasena
      });

      if (!cuerpo.success) {
        throw new Error(cuerpo.error || 'No fue posible cambiar la contraseña.');
      }

      Swal.fire({
        title: '¡Éxito!',
        text: cuerpo.mensaje || 'Contraseña actualizada correctamente.',
        icon: 'success'
      });
      setShowPasswordModal(false);
    } catch (error) {
      console.error('❌ Error al cambiar la contraseña de la coordinadora desde la vista de directora:', error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Ocurrió un error al cambiar la contraseña.',
        icon: 'error'
      });
    }
  };

  const handleRestaurar = () => {
    if (datos.idUsuario) {
      cargarDatos(datos.idUsuario);
      return;
    }

    const idDesdeSeleccion = parsePositiveNumber(selectedId);
    const idInicial = idDesdeSeleccion || obtenerIdInicialCoordinadora();

    if (idInicial) {
  cargarDatos(idInicial);
      return;
    }

    setDatos(camposIniciales);
    setIsModified(false);
    setSelectedId('');
    setSearchTerm('');
    setSuggestions([]);
    setShowSuggestions(false);
    setSearchError('');
    setSearchingSuggestions(false);
  };

  if (loading) {
    return (
      <div className="container py-4 px-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-3">Cargando información de la coordinadora...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container py-4 px-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <img src={logoInstituto} alt="Logo Instituto" className="logo" />
          <div className="flex-grow-1 text-center">
            <h4 className="mb-0">Instituto Silviano Carrillo</h4>
            <small className="text-muted fs-5">Consulta y edición de coordinadora</small>
          </div>
          <img src={logoSEP} alt="Logo SEP" className="logo" />
        </div>

        <div className="p-3 bg-white border rounded shadow-sm mb-3">
          <form className="row g-3 align-items-end" onSubmit={handleSearchSubmit}>
            <div className="col-md-6 col-lg-5 position-relative">
              <label className="form-label">Buscar coordinadora</label>
              <div className="input-group">
                <span className="input-group-text">
                  {searchingSuggestions ? (
                    <span className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Buscando...</span>
                    </span>
                  ) : (
                    <i className="fas fa-search" aria-hidden="true" />
                  )}
                </span>
                <input
                  type="text"
                  className="form-control"
                  value={searchTerm}
                  onChange={handleSearchTermChange}
                  onFocus={handleSearchFocus}
                  onBlur={handleSearchBlur}
                  placeholder="Escribe el nombre o ID"
                  aria-label="Buscar coordinadora"
                />
              </div>
              <div className="form-text">Escribe al menos 2 letras para ver sugerencias relacionadas.</div>
              {searchError && <div className="text-danger small mt-1">{searchError}</div>}
              {showSuggestions && (
                <div
                  className="list-group position-absolute w-100 shadow-sm mt-1"
                  style={{ zIndex: 1050, maxHeight: '240px', overflowY: 'auto' }}
                >
                  {searchingSuggestions ? (
                    <div className="list-group-item text-center py-2">
                      <span className="spinner-border spinner-border-sm text-primary me-2" role="status">
                        <span className="visually-hidden">Buscando...</span>
                      </span>
                      Buscando coincidencias...
                    </div>
                  ) : suggestions.length ? (
                    suggestions.map((sugerencia) => (
                      <button
                        key={sugerencia.idUsuario}
                        type="button"
                        className="list-group-item list-group-item-action text-start"
                        onClick={() => manejarSeleccionSugerencia(sugerencia)}
                      >
                        <div className="fw-semibold">{sugerencia.displayName || construirNombreCompleto(sugerencia)}</div>
                        <small className="text-muted">
                          ID: {sugerencia.idUsuario}
                          {sugerencia.usuario ? ` · Usuario: ${sugerencia.usuario}` : ''}
                        </small>
                      </button>
                    ))
                  ) : (
                    <div className="list-group-item text-muted">No se encontraron resultados.</div>
                  )}
                </div>
              )}
            </div>

            <div className="col-sm-6 col-md-4 col-lg-3">
              <label className="form-label">ID seleccionado</label>
              <input
                type="text"
                className="form-control"
                value={selectedId || ''}
                readOnly
                placeholder="Sin seleccionar"
              />
              <div className="form-text">Se carga automáticamente al elegir una opción.</div>
            </div>

            <div className="col-auto d-flex gap-2">
              <button type="submit" className="btn btn-primary">
                <i className="fas fa-arrow-circle-right me-2" /> Cargar datos
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={handleRestaurar}>
                <i className="fas fa-undo me-2" /> Restaurar
              </button>
            </div>

            <div className="col-auto ms-auto">
              <button type="button" className="btn btn-danger" onClick={handleSalir}>
                <i className="fas fa-sign-out-alt me-2" /> Salir
              </button>
            </div>
          </form>
        </div>

        <div className="p-4 bg-light rounded shadow-sm">
          <form className="row g-3" onSubmit={handleSubmit}>
            <div className="col-md-3">
              <label className="form-label">ID Coordinadora</label>
              <input type="text" className="form-control" value={datos.idUsuario || ''} readOnly />
            </div>

            <div className="col-md-3">
              <label className="form-label">Tipo de Usuario</label>
              <input type="text" className="form-control" value={datos.tipoUsuario || 'Coordinadora'} readOnly />
            </div>

            <div className="col-md-6">
              <label className="form-label">Nombre(s)</label>
              <input
                type="text"
                className="form-control"
                name="nombre"
                value={datos.nombre || ''}
                onChange={handleInputChange}
                disabled={!datos.idUsuario}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Apellido Paterno</label>
              <input
                type="text"
                className="form-control"
                name="apellidoPaterno"
                value={datos.apellidoPaterno || ''}
                onChange={handleInputChange}
                disabled={!datos.idUsuario}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Apellido Materno</label>
              <input
                type="text"
                className="form-control"
                name="apellidoMaterno"
                value={datos.apellidoMaterno || ''}
                onChange={handleInputChange}
                disabled={!datos.idUsuario}
              />
            </div>

            <div className="col-md-2">
              <label className="form-label">Sexo</label>
              <select
                className="form-select"
                name="sexo"
                value={datos.sexo || 'F'}
                onChange={handleInputChange}
                disabled={!datos.idUsuario}
              >
                <option value="F">Femenino</option>
                <option value="M">Masculino</option>
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label">Fecha de Nacimiento</label>
              <input
                type="date"
                className="form-control"
                name="fechaNacimiento"
                value={datos.fechaNacimiento || ''}
                onChange={handleInputChange}
                disabled={!datos.idUsuario}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Teléfono</label>
              <input
                type="text"
                className="form-control"
                name="telefono"
                value={datos.telefono || ''}
                onChange={handleTelefonoChange}
                placeholder="Máximo 10 dígitos"
                disabled={!datos.idUsuario}
              />
              <div className="form-text">Solo números, máximo 10 dígitos.</div>
            </div>

            <div className="col-md-4">
              <label className="form-label">Correo electrónico</label>
              <input
                type="email"
                className="form-control"
                name="correo"
                value={datos.correo || ''}
                onChange={handleInputChange}
                disabled={!datos.idUsuario}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Puesto</label>
              <input
                type="text"
                className="form-control"
                name="puesto"
                value={datos.puesto || ''}
                onChange={handleInputChange}
                disabled={!datos.idUsuario}
              />
            </div>

            <div className="col-12 text-end mt-3">
              <button
                type="button"
                className="btn btn-info px-4 me-2"
                onClick={abrirModalContrasena}
                disabled={!datos.idUsuario}
              >
                Cambiar contraseña
              </button>
              <button
                type="button"
                className="btn btn-secondary px-4 me-2"
                onClick={handleRestaurar}
                disabled={!datos.idUsuario}
              >
                Restaurar
              </button>
              <button
                type="submit"
                className={`btn px-4 ${isModified && datos.idUsuario ? 'btn-primary' : 'btn-outline-secondary'}`}
                disabled={!isModified || !datos.idUsuario}
              >
                {isModified && datos.idUsuario ? 'Guardar cambios' : 'Sin cambios'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {showPasswordModal && (
        <div className="modal fade show" style={{ display: 'block' }} aria-modal="true" role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Cambiar contraseña</h5>
                <button type="button" className="btn-close" onClick={() => setShowPasswordModal(false)} aria-label="Close"></button>
              </div>
              <div className="modal-body">
                <form onSubmit={(e) => e.preventDefault()}>
                  <div className="mb-3">
                    <label className="form-label">Contraseña actual</label>
                    <div className="input-group">
                      <input
                        type={showPasswords.actual ? 'text' : 'password'}
                        className="form-control"
                        value={passwordData.contrasenaActual}
                        onChange={(e) => handlePasswordInputChange('contrasenaActual', e.target.value)}
                        required
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => togglePasswordVisibility('actual')}
                      >
                        <i className={`fas ${showPasswords.actual ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Nueva contraseña</label>
                    <div className="input-group">
                      <input
                        type={showPasswords.nueva ? 'text' : 'password'}
                        className="form-control"
                        value={passwordData.nuevaContrasena}
                        onChange={(e) => handlePasswordInputChange('nuevaContrasena', e.target.value)}
                        required
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => togglePasswordVisibility('nueva')}
                      >
                        <i className={`fas ${showPasswords.nueva ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Confirmar nueva contraseña</label>
                    <div className="input-group">
                      <input
                        type={showPasswords.confirmar ? 'text' : 'password'}
                        className={`form-control ${
                          passwordData.confirmarContrasena && !contrasenasCoinciden ? 'is-invalid' : ''
                        }`}
                        value={passwordData.confirmarContrasena}
                        onChange={(e) => handlePasswordInputChange('confirmarContrasena', e.target.value)}
                        required
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => togglePasswordVisibility('confirmar')}
                      >
                        <i className={`fas ${showPasswords.confirmar ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                    {passwordData.confirmarContrasena && !contrasenasCoinciden && (
                      <div className="invalid-feedback">Las contraseñas no coinciden.</div>
                    )}
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowPasswordModal(false)}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={cambiarContrasena}
                  disabled={
                    !passwordData.contrasenaActual.trim() ||
                    !passwordData.nuevaContrasena.trim() ||
                    !passwordData.confirmarContrasena.trim() ||
                    !contrasenasCoinciden
                  }
                >
                  Cambiar contraseña
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPasswordModal && (
        <div className="modal-backdrop fade show" onClick={() => setShowPasswordModal(false)} />
      )}
    </>
  );
};

function resolveRolBase(pathname) {
  const roles = ['directora', 'coordinadora', 'docente', 'alumno'];
  const segmento = pathname.split('/').filter(Boolean)[0];
  if (roles.includes(segmento)) return segmento;

  if (typeof window !== 'undefined') {
    const roleId = Number(window.localStorage.getItem('role'));
    const mapa = { 1: 'directora', 2: 'coordinadora', 3: 'docente', 4: 'alumno' };
    return mapa[roleId] || null;
  }

  return null;
}

export default ConsultarCoordinador;